/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { SubDepartment } from "types/models/menu"
import { MenuOverviewRow } from "components/common"
import { getSellingTimeByID } from "utils/selling_time_helper"
import { computeSubDeptCheckboxState } from "utils/bulk_selection_helpers"
import { useSellingTimes } from "hooks/useSellingTimes"
import { MenuSubDepartmentRowActions } from "./shared/MenuRowSelectionControls"

interface IProps {
  subDepartment: SubDepartment
}

export const MenuSubDepartmentRowV3: FC<IProps> = ({
  subDepartment,
}: IProps) => {
  const showItemSelection = useSelector(
    (state: AppState) => state.menu.drawer.showItemSelection,
  )
  const { sellingTimes } = useSellingTimes()
  const bulkSelectionsV3 = useSelector(
    (state: AppState) => state.bulkSelectionsV3,
  )

  const dispatch = useDispatch()

  // V3 mode: Calculate checkbox state from BulkSelectionsMetadata
  const { checked, indeterminate } = useMemo(() => {
    return computeSubDeptCheckboxState(
      bulkSelectionsV3,
      subDepartment.itemClassID ?? "",
      subDepartment.departmentID ?? "",
      subDepartment.totalItems ?? 0,
    )
  }, [subDepartment, bulkSelectionsV3])

  const disableCheckbox = (subDepartment.totalItems ?? 0) === 0

  const handleCheck = (value: boolean) => {
    dispatch(
      actionCreator.bulkSelectionsV3.multiSelectSubDepartment({
        subDepartment,
        value,
      }),
    )
  }

  const activeSubDepartment = useSelector(
    (state: AppState) => state.menu.overview.subDepartment,
  )
  const isActiveSubDepartment =
    subDepartment.itemClassID === activeSubDepartment?.itemClassID

  return (
    <MenuOverviewRow
      name={subDepartment.itemClassName ?? ""}
      isActive={isActiveSubDepartment}
      count={subDepartment.totalItems ?? 0}
      countNotAvailable={subDepartment.totalUnavailableItems}
      sellingTime={getSellingTimeByID(
        subDepartment.sellingTimeID,
        sellingTimes,
      )}
      renderAfter={
        <MenuSubDepartmentRowActions
          subDepartment={subDepartment}
          showItemSelection={showItemSelection}
          disableCheckbox={disableCheckbox}
          checked={checked}
          indeterminate={indeterminate}
          onCheck={handleCheck}
        />
      }
    />
  )
}
